import { Badge } from "@/components/ui/badge";
import { Pencil, Plus, Phone, MessageCircle, Mail, IdCard, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PhoneWithOperator } from "@/components/ui/phone-with-operator";
import { CopyOnClick } from "@/components/planilha/CopyOnClick";

interface GestorCessionarioBadgeProps {
  tipo: 'gestor' | 'cessionario';
  nome?: string;
  telefone?: string;
  whatsapp?: string;
  email?: string;
  rg?: string;
  cpf?: string;
  onEdit?: () => void;
  variant?: 'default' | 'inline' | 'columns';
}

export function GestorCessionarioBadge({ tipo, nome, telefone, whatsapp, email, rg, cpf, onEdit, variant = 'default' }: GestorCessionarioBadgeProps) {
  const label = tipo === 'gestor' ? 'Gestor' : 'Cedente';
  
  if (!nome) {
    return (
      <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
        <span className="text-sm text-muted-foreground">
          {label} não cadastrado
        </span>
        {onEdit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="h-7 px-2"
          >
            <Plus className="h-3 w-3 mr-1" />
            Adicionar
          </Button>
        )}
      </div>
    );
  }

  // Layout inline compacto (horizontal)
  if (variant === 'inline') {
    return (
      <div className="flex items-center gap-2 text-sm flex-wrap">
        <CopyOnClick value={nome}>
          <span className="font-medium">{nome}</span>
        </CopyOnClick>
        <Badge variant="outline" className="text-xs">{label}</Badge>
        
        {telefone && <><span className="text-muted-foreground">•</span><CopyOnClick value={telefone.replace(/\D/g, '')}><PhoneWithOperator phone={telefone} variant="inline"className="text-sm"/></CopyOnClick></>}
        {whatsapp && <><span className="text-muted-foreground">•</span><CopyOnClick value={whatsapp.replace(/\D/g, '')}><PhoneWithOperator phone={whatsapp} variant="inline"className="text-sm"/></CopyOnClick></>}
        {email && (
          <>
            <span className="text-muted-foreground">•</span>
            <CopyOnClick value={email}>
              <span>{email}</span>
            </CopyOnClick>
          </>
        )}
        {cpf && (
          <>
            <span className="text-muted-foreground">•</span>
            <CopyOnClick value={cpf}>
              <span>CPF: {cpf}</span>
            </CopyOnClick>
          </>
        )}
        {rg && (
          <>
            <span className="text-muted-foreground">•</span>
            <CopyOnClick value={rg}>
              <span>RG: {rg}</span>
            </CopyOnClick>
          </>
        )}
        
        {onEdit && (
          <Button variant="ghost" size="sm" onClick={onEdit} className="h-6 px-2 ml-2">
            <Pencil className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  // Layout em coluna compacta (para uso em grid de 2 colunas)
  if (variant === 'columns') {
    const faltaDocumento = tipo === 'gestor'
      ? [!cpf?.trim() && 'CPF', !rg?.trim() && 'RG'].filter((c): c is string => !!c)
      : [];
    return (
      <div className="flex flex-col gap-[5px] rounded-[9px] border border-border-warm bg-card px-2.5 py-2">
        {/* Nome PRIMEIRO, papel como badge ao lado — o papel não é cabeçalho. */}
        <div className="mb-0.5 flex items-center gap-2">
          <CopyOnClick value={nome}>
            <span className="text-[11.5px] font-semibold">{nome}</span>
          </CopyOnClick>
          <Badge variant="outline" className="border-border bg-accent px-1.5 py-0 text-[9px] font-bold uppercase tracking-[0.4px] text-muted-foreground">
            {label}
          </Badge>
        </div>

        {/* Rótulo 9px/700 caixa alta + valor 10.5px/600, cada linha com seu
            ícone lucide.  CPF e RG usavam o MESMO ícone — dois campos diferentes
            com o mesmo símbolo; agora IdCard e CreditCard.
            O `whatsapp` é campo PRÓPRIO: o `calcularPercentualPreenchimento`
            conta separado do telefone (um registro pode ter um e não o outro) e
            é o número em que o operador manda mensagem. Nunca fundir, nunca
            omitir. */}
        <div className="space-y-[5px]">
          {telefone && (
            <div className="flex items-center gap-1.5">
              <Phone className="h-3 w-3 flex-shrink-0 text-muted-foreground/70" />
              <span className="w-[54px] flex-shrink-0 text-[9px] font-bold uppercase tracking-[0.6px] text-muted-foreground/70">Tel</span>
              <CopyOnClick value={telefone.replace(/\D/g, '')}>
                <PhoneWithOperator phone={telefone} variant="readonly" className="text-[10.5px] font-semibold" />
              </CopyOnClick>
            </div>
          )}
          {whatsapp && (
            <div className="flex items-center gap-1.5">
              <MessageCircle className="h-3 w-3 flex-shrink-0 text-muted-foreground/70" />
              <span className="w-[54px] flex-shrink-0 text-[9px] font-bold uppercase tracking-[0.6px] text-muted-foreground/70">WhatsApp</span>
              <CopyOnClick value={whatsapp.replace(/\D/g, '')}>
                <PhoneWithOperator phone={whatsapp} variant="readonly" className="text-[10.5px] font-semibold" />
              </CopyOnClick>
            </div>
          )}
          {email && (
            <div className="flex min-w-0 items-center gap-1.5">
              <Mail className="h-3 w-3 flex-shrink-0 text-muted-foreground/70" />
              <span className="w-[54px] flex-shrink-0 text-[9px] font-bold uppercase tracking-[0.6px] text-muted-foreground/70">E-mail</span>
              <CopyOnClick value={email}>
                <span className="block truncate text-[10.5px] font-semibold">{email}</span>
              </CopyOnClick>
            </div>
          )}
          {cpf && (
            <div className="flex items-center gap-1.5">
              <IdCard className="h-3 w-3 flex-shrink-0 text-muted-foreground/70" />
              <span className="w-[54px] flex-shrink-0 text-[9px] font-bold uppercase tracking-[0.6px] text-muted-foreground/70">CPF</span>
              <CopyOnClick value={cpf}>
                <span className="text-[10.5px] font-semibold tabular-nums">{cpf}</span>
              </CopyOnClick>
            </div>
          )}
          {rg && (
            <div className="flex items-center gap-1.5">
              <CreditCard className="h-3 w-3 flex-shrink-0 text-muted-foreground/70" />
              <span className="w-[54px] flex-shrink-0 text-[9px] font-bold uppercase tracking-[0.6px] text-muted-foreground/70">RG</span>
              <CopyOnClick value={rg}>
                <span className="text-[10.5px] font-semibold tabular-nums">{rg}</span>
              </CopyOnClick>
            </div>
          )}
        </div>
        
        {faltaDocumento.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {faltaDocumento.map((campo) => (
              <span key={campo} className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                Falta {campo}
              </span>
            ))}
          </div>
        )}

        {onEdit && (faltaDocumento.length > 0 ? (
          <Button size="sm" onClick={onEdit} className="h-7 px-2.5 mt-1 self-start">
            <Pencil className="h-3 w-3 mr-1" />
            Completar {faltaDocumento.join(' e ')}
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={onEdit} className="h-6 px-2 mt-1">
            <Pencil className="h-3 w-3 mr-1" />
            Editar
          </Button>
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
      <div className="flex items-center gap-3">
        <div>
          <div className="flex items-center gap-2">
            <CopyOnClick value={nome}>
              <span className="font-medium text-sm">{nome}</span>
            </CopyOnClick>
            <Badge variant="outline" className="text-xs">{label}</Badge>
          </div>
          {(telefone || email) && (
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
              {telefone && <span className="flex items-center gap-1"><CopyOnClick value={telefone.replace(/\D/g, '')}><PhoneWithOperator phone={telefone} variant="inline"className="text-xs"/></CopyOnClick></span>}
              {telefone && email && <span>•</span>}
              {email && (
                <CopyOnClick value={email}>
                  <span>{email}</span>
                </CopyOnClick>
              )}
            </div>
          )}
        </div>
      </div>
      {onEdit && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="h-7 px-2"
        >
          <Pencil className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}
